import type { OrgRow, TableContent, TableRanking } from "./orgRows";
import type { LawyerRow } from "./lawyerRows";

/** Key of the top block: content attached to the practice area, not to any ranking table.
 * The same string staff-bff keys `contentByTable` with. */
export const UNTABLED_SECTION_KEY = "__untabled__";

/** Header for the top block once ranking-table sections exist beside it. */
export const UNTABLED_SECTION_NAME = "Not ranked";

function notStartedContent(totalFieldsPerEntity: number): TableContent {
  return {
    reviewStatus: null,
    hasSubmission: false,
    totalFields: totalFieldsPerEntity,
    populatedFields: 0,
  };
}

/** A row the BFF attaches per-table rankings and content attribution to.
 * `contentByTable` is keyed by table uuid, plus `UNTABLED_SECTION_KEY`. */
export type RankableRow = {
  reviewStatus: string | null;
  hasSubmission: boolean;
  totalFields: number;
  populatedFields: number;
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  contentTableIds: string[];
  contentByTable: Record<string, TableContent>;
};

/** A table section falls back to the row's untabled content, then (when the entity has no content
 * in any section it is placed in) to the content filed against another list, before not-started:
 * an untabled row belongs to the practice area, so it follows the entity into every section it
 * appears in. */
function tableContentFor<T extends RankableRow>(
  row: T,
  uuid: string,
  placedSectionKeys: ReadonlySet<string>,
  totalFieldsPerEntity: number,
): TableContent {
  const own = row.contentByTable[uuid] ?? row.contentByTable[UNTABLED_SECTION_KEY];
  if (own) return own;
  const heldInAPlacedSection = [...placedSectionKeys].some(key =>
    Object.hasOwn(row.contentByTable, key),
  );
  if (heldInAPlacedSection) return notStartedContent(totalFieldsPerEntity);
  return bestContentAcrossTables(row, totalFieldsPerEntity);
}

/** One row as it appears inside a single section. */
export type SectionRow<T> = T & {
  /** Null when the section is no table at all: the top block, or a trailing unlabelled group. */
  sectionTableName: string | null;
  sectionTier: number | null;
  sectionRankingType: string | null;
  sectionPreviousTier: number | null;
  sectionPreviousRankingType: string | null;
  /** The table id a detail-page link carries, null where it must carry none. */
  sectionTableId: string | null;
  /** The section's true identity, unique per rendered row. Never forced null the way
   * `sectionTableId` is, so rows in different sections cannot collide on the row key. */
  sectionKey: string;
  /** Whether this table accepts submissions. False for firm_to_watch and similar non-submittable
   * tables where submission/progress columns are meaningless. */
  sectionIsSubmittable: boolean;
};

export type Section<T> = { name: string; rows: SectionRow<T>[] };

/** The BPA's configured ranking tables, as the ranking-tables endpoint orders them.
 * `id` and `wpId` are optional so older table-order fixtures still compile. */
export type RankingTableInfo = {
  id?: string;
  name: string;
  tableOrder: number;
  subtype?: string;
  submissionType?: string | null;
  wpId?: number | null;
};
export type RankingTableOrder = RankingTableInfo[];

type SectionSummary = { tier: number | null; rankingType: string | null };

type Attribution = { current?: TableRanking; previous?: TableRanking };

const UNTIERED_RANK = Number.MAX_SAFE_INTEGER;
const NO_TIER_RANK = Number.POSITIVE_INFINITY;

/** Mirrors staff-bff: tiers 1-8 are the bands (lowest best), tier 0 is untiered-but-ranked
 * and sorts after them, no ranking sorts last. */
function tierRank(tier: number | null): number {
  if (tier === null) return NO_TIER_RANK;
  if (tier === 0) return UNTIERED_RANK;
  return tier;
}

function isBetter(candidate: TableRanking, current: TableRanking | undefined): boolean {
  return current === undefined || tierRank(candidate.tier) < tierRank(current.tier);
}

function buildWpIdToUuid(tables: RankingTableOrder): Map<number, string> {
  const map = new Map<number, string>();
  for (const table of tables) {
    if (table.id && table.wpId !== null && table.wpId !== undefined) {
      map.set(table.wpId, table.id);
    }
  }
  return map;
}

/** A null id is the expected pre-sync state and falls back to the main table silently;
 * an id naming no configured table is a mapping gap and is reported. */
function resolveSectionKey(
  tableId: number | string | null | undefined,
  tableName: string | null | undefined,
  wpIdToUuid: Map<number, string>,
  knownTableIds: Set<string>,
  nameToTableId: Map<string, string>,
  mainTableId: string | null,
  onUnmappedTableId: (wpTableId: number) => void,
): string | null {
  if (typeof tableId === "string") {
    if (knownTableIds.has(tableId)) return tableId;
    if (tableName) {
      const mapped = nameToTableId.get(tableName);
      if (mapped) return mapped;
    }
    return mainTableId;
  }
  if (tableId != null) {
    const uuid = wpIdToUuid.get(tableId);
    if (uuid !== undefined) return uuid;
    onUnmappedTableId(tableId);
  }
  return mainTableId;
}

function ignoreUnmappedTableId(): void {
  // Default sink when a caller supplies none.
}

function pushTo<T>(map: Map<string, SectionRow<T>[]>, key: string, row: SectionRow<T>): void {
  const existing = map.get(key);
  if (existing) existing.push(row);
  else map.set(key, [row]);
}

/** A real ranking is a tier on a tiered list, or tier 0 on a list without tiers. staff-bff turns the
 * list's own flag into tier 0 versus no tier, so a tierless ranking on a tiered list is not one. */
function isRealRanking(entry: TableRanking): boolean {
  return entry.tier !== null;
}

/** Every section key a row belongs to, placed by real ranking alone. Content filed against a list
 * places nothing. Untabled content follows the row into the sections it already has; a row with no
 * real ranking is placed in the untabled "Not ranked" section. */
function collectAttributions<T extends RankableRow>(
  row: T,
  wpIdToUuid: Map<number, string>,
  knownTableIds: Set<string>,
  nameToTableId: Map<string, string>,
  mainTableId: string | null,
  onUnmappedTableId: (wpTableId: number) => void,
): Map<string, Attribution> {
  const byKey = new Map<string, Attribution>();

  function ensure(key: string): Attribution {
    let entry = byKey.get(key);
    if (!entry) {
      entry = {};
      byKey.set(key, entry);
    }
    return entry;
  }

  for (const entry of row.currentRankings.filter(isRealRanking)) {
    const key = resolveSectionKey(
      entry.tableId,
      entry.tableName,
      wpIdToUuid,
      knownTableIds,
      nameToTableId,
      mainTableId,
      onUnmappedTableId,
    );
    if (key === null) continue;
    const slot = ensure(key);
    if (isBetter(entry, slot.current)) slot.current = entry;
  }

  for (const entry of row.previousRankings.filter(isRealRanking)) {
    const key = resolveSectionKey(
      entry.tableId,
      entry.tableName,
      wpIdToUuid,
      knownTableIds,
      nameToTableId,
      mainTableId,
      onUnmappedTableId,
    );
    if (key === null) continue;
    const slot = ensure(key);
    if (isBetter(entry, slot.previous)) slot.previous = entry;
  }

  if (byKey.size === 0) {
    ensure(UNTABLED_SECTION_KEY);
  }

  return byKey;
}

/** A Not ranked row shows its untabled content, else the content filed against any list; a row
 * holding no content at all keeps the figures it arrived with. */
function notRankedContentFor<T extends RankableRow>(
  row: T,
  totalFieldsPerEntity: number,
): Partial<TableContent> {
  if (Object.keys(row.contentByTable).length === 0) return {};
  return (
    row.contentByTable[UNTABLED_SECTION_KEY] ?? bestContentAcrossTables(row, totalFieldsPerEntity)
  );
}

function untabledSectionRow<T extends RankableRow>(
  row: T,
  current: SectionSummary,
  previous: SectionSummary,
  totalFieldsPerEntity: number,
): SectionRow<T> {
  return {
    ...row,
    ...notRankedContentFor(row, totalFieldsPerEntity),
    sectionTableName: null,
    sectionTier: current.tier,
    sectionRankingType: current.rankingType,
    sectionPreviousTier: previous.tier,
    sectionPreviousRankingType: previous.rankingType,
    sectionTableId: null,
    sectionKey: UNTABLED_SECTION_KEY,
    sectionIsSubmittable: true,
  };
}

function bestContentAcrossTables<T extends RankableRow>(
  row: T,
  totalFieldsPerEntity: number,
): TableContent {
  const entries = Object.values(row.contentByTable);
  const withSubmission = entries.find(c => c.hasSubmission);
  if (withSubmission) return withSubmission;
  const withProgress = entries.find(c => c.populatedFields > 0);
  if (withProgress) return withProgress;
  return entries[0] ?? notStartedContent(totalFieldsPerEntity);
}

function tableSectionRow<T extends RankableRow>(
  row: T,
  uuid: string,
  table: RankingTableInfo | undefined,
  attribution: Attribution,
  placedSectionKeys: ReadonlySet<string>,
  forceNoLink: boolean,
  totalFieldsPerEntity: number,
): SectionRow<T> {
  const isSubmittable = table?.submissionType === "firm" || table?.submissionType === "lawyer";
  const content = isSubmittable
    ? tableContentFor(row, uuid, placedSectionKeys, totalFieldsPerEntity)
    : bestContentAcrossTables(row, totalFieldsPerEntity);
  return {
    ...row,
    ...content,
    sectionTableName: table?.name ?? uuid,
    sectionTier: attribution.current?.tier ?? null,
    sectionRankingType: attribution.current?.rankingType ?? null,
    sectionPreviousTier: attribution.previous?.tier ?? null,
    sectionPreviousRankingType: attribution.previous?.rankingType ?? null,
    sectionTableId: forceNoLink ? null : uuid,
    sectionKey: uuid,
    sectionIsSubmittable: isSubmittable,
  };
}

/** The top block leads, then the main table, then each table's own `tableOrder`. */
function sectionRank(
  sectionKey: string,
  mainTableId: string | null,
  tableById: Map<string, RankingTableInfo>,
): number {
  if (sectionKey === mainTableId) return -1;
  if (sectionKey === UNTABLED_SECTION_KEY) return Number.MAX_SAFE_INTEGER - 1;
  return tableById.get(sectionKey)?.tableOrder ?? Number.MAX_SAFE_INTEGER;
}

function sectionNameOf(
  sectionKey: string,
  mainTableId: string | null,
  mainTableLabel: string,
  tableById: Map<string, RankingTableInfo>,
): string {
  if (sectionKey === UNTABLED_SECTION_KEY) return UNTABLED_SECTION_NAME;
  if (sectionKey === mainTableId) return mainTableLabel;
  return tableById.get(sectionKey)?.name ?? sectionKey;
}

function sortedSectionEntries<T>(
  bySectionKey: Map<string, SectionRow<T>[]>,
  mainTableId: string | null,
  mainTableLabel: string,
  tableById: Map<string, RankingTableInfo>,
): Section<T>[] {
  return [...bySectionKey.entries()]
    .sort(([a], [b]) => {
      const byRank =
        sectionRank(a, mainTableId, tableById) - sectionRank(b, mainTableId, tableById);
      if (byRank !== 0) return byRank;
      return sectionNameOf(a, mainTableId, mainTableLabel, tableById).localeCompare(
        sectionNameOf(b, mainTableId, mainTableLabel, tableById),
      );
    })
    .map(([sectionKey, rows]) => ({
      name: sectionNameOf(sectionKey, mainTableId, mainTableLabel, tableById),
      rows,
    }));
}

/** Unique per rendered row: a row in two sections has a row key in each. */
export function sectionRowKey(id: string, sectionKey: string): string {
  return `${id}::${sectionKey}`;
}

export type SectionOptions<T> = {
  tables: RankingTableOrder;
  /** Names the main-table section: the table sharing the practice area's name. */
  mainTableLabel: string;
  currentSummary: (row: T) => SectionSummary;
  previousSummary: (row: T) => SectionSummary;
  /** research-api's denominator for an entity holding no content row for this edition. */
  totalFieldsPerEntity: number;
  /** Called per ranking entry whose WordPress table id names no configured table — a real
   * research_wp_mapping gap, never the pre-sync null-id state. */
  onUnmappedTableId?: (wpTableId: number) => void;
};

/** "Not ranked" holds every row with no real ranking in either edition.
 * A lone section is returned unnamed, so it renders headerless. */
export function toSections<T extends RankableRow>(
  rows: T[],
  options: SectionOptions<T>,
): Section<T>[] {
  const wpIdToUuid = buildWpIdToUuid(options.tables);
  const tableById = new Map(
    options.tables.flatMap(table => (table.id ? [[table.id, table] as const] : [])),
  );
  const knownTableIds = new Set(tableById.keys());
  const nameToTableId = new Map(
    options.tables.flatMap(table =>
      table.id && table.name ? [[table.name, table.id] as const] : [],
    ),
  );
  const mainTableId =
    options.tables.find(table => table.name === options.mainTableLabel)?.id ?? null;
  const onUnmappedTableId = options.onUnmappedTableId ?? ignoreUnmappedTableId;
  const perEntity = options.totalFieldsPerEntity;

  const bySectionKey = new Map<string, SectionRow<T>[]>();

  for (const row of rows) {
    const attributions = collectAttributions(
      row,
      wpIdToUuid,
      knownTableIds,
      nameToTableId,
      mainTableId,
      onUnmappedTableId,
    );

    for (const [sectionKey, attribution] of attributions) {
      const sectioned =
        sectionKey === UNTABLED_SECTION_KEY
          ? untabledSectionRow(
              row,
              options.currentSummary(row),
              options.previousSummary(row),
              perEntity,
            )
          : tableSectionRow(
              row,
              sectionKey,
              tableById.get(sectionKey),
              attribution,
              new Set(attributions.keys()),
              false,
              perEntity,
            );
      pushTo(bySectionKey, sectionKey, sectioned);
    }
  }

  const sections = sortedSectionEntries(
    bySectionKey,
    mainTableId,
    options.mainTableLabel,
    tableById,
  );

  if (sections.length === 1) {
    return [{ name: "", rows: sections[0].rows }];
  }

  return sections;
}

// ── Organisations ──

export type OrgSectionRow = SectionRow<OrgRow>;
export type OrgSection = Section<OrgRow>;
export type OrgSectionOptions = {
  tables: RankingTableOrder;
  mainTableLabel: string;
  totalFieldsPerEntity: number;
  onUnmappedTableId?: (wpTableId: number) => void;
};

/** The one firm-grain subtype a firm cannot submit against. */
const WATCH_SUBTYPE = "firm_to_watch";

function orgCurrentSummary(row: OrgRow): SectionSummary {
  return { tier: row.currentRankingTier, rankingType: row.currentRankingType };
}

function orgPreviousSummary(row: OrgRow): SectionSummary {
  return { tier: row.previousRankingTier, rankingType: row.previousRankingType };
}

/** `toSections` plus watch-table handling: a `firm_to_watch` section sorts after every
 * submission table, because a firm cannot submit into one. */
export function toOrgSections(rows: OrgRow[], options: OrgSectionOptions): OrgSection[] {
  const wpIdToUuid = buildWpIdToUuid(options.tables);
  const tableById = new Map(
    options.tables.flatMap(table => (table.id ? [[table.id, table] as const] : [])),
  );
  const knownTableIds = new Set(tableById.keys());
  const nameToTableId = new Map(
    options.tables.flatMap(table =>
      table.id && table.name ? [[table.name, table.id] as const] : [],
    ),
  );
  const mainTableId =
    options.tables.find(table => table.name === options.mainTableLabel)?.id ?? null;
  const onUnmappedTableId = options.onUnmappedTableId ?? ignoreUnmappedTableId;
  const perEntity = options.totalFieldsPerEntity;
  const isWatchUuid = (uuid: string): boolean => tableById.get(uuid)?.subtype === WATCH_SUBTYPE;

  const bySectionKey = new Map<string, OrgSectionRow[]>();
  const watchBySectionKey = new Map<string, OrgSectionRow[]>();
  const notRankedBySectionKey = new Map<string, OrgSectionRow[]>();

  for (const row of rows) {
    const attributions = collectAttributions(
      row,
      wpIdToUuid,
      knownTableIds,
      nameToTableId,
      mainTableId,
      onUnmappedTableId,
    );

    for (const [sectionKey, attribution] of attributions) {
      if (sectionKey === UNTABLED_SECTION_KEY) {
        pushTo(
          notRankedBySectionKey,
          sectionKey,
          untabledSectionRow(row, orgCurrentSummary(row), orgPreviousSummary(row), perEntity),
        );
        continue;
      }
      // A watch table accepts no submission, so its rows link with no table param.
      const watch = isWatchUuid(sectionKey);
      const sectioned = tableSectionRow(
        row,
        sectionKey,
        tableById.get(sectionKey),
        attribution,
        new Set(attributions.keys()),
        watch,
        perEntity,
      );
      pushTo(watch ? watchBySectionKey : bySectionKey, sectionKey, sectioned);
    }
  }

  const sections = sortedSectionEntries(
    bySectionKey,
    mainTableId,
    options.mainTableLabel,
    tableById,
  );
  sections.push(
    ...sortedSectionEntries(watchBySectionKey, mainTableId, options.mainTableLabel, tableById),
    ...sortedSectionEntries(notRankedBySectionKey, mainTableId, options.mainTableLabel, tableById),
  );

  if (sections.length === 1) {
    return [{ name: "", rows: sections[0].rows }];
  }

  return sections;
}

export function orgSectionRowKey(row: OrgSectionRow): string {
  return sectionRowKey(row.companyUuid, row.sectionKey);
}

// ── Lawyers ──

export type LawyerSectionRow = SectionRow<LawyerRow>;
export type LawyerSection = Section<LawyerRow>;
export type LawyerSectionOptions = {
  tables: RankingTableOrder;
  mainTableLabel: string;
  totalFieldsPerEntity: number;
  onUnmappedTableId?: (wpTableId: number) => void;
};

/** Lawyers have no watch-list equivalent, so every table is a normal section. */
export function toLawyerSections(
  rows: LawyerRow[],
  options: LawyerSectionOptions,
): LawyerSection[] {
  return toSections(rows, {
    ...options,
    currentSummary: row => ({ tier: row.currentTier, rankingType: row.currentRankingType }),
    previousSummary: row => ({ tier: row.previousTier, rankingType: row.previousRankingType }),
  });
}

export function lawyerSectionRowKey(row: LawyerSectionRow): string {
  return sectionRowKey(row.contactUuid, row.sectionKey);
}
